import { useMemo, useState } from 'react';

import {
  type ColorToken,
  CountPill,
  DonutChart,
  LabeledFilterSelect,
  LegendRow,
  StackedBar,
  type StackedBarSegment,
  VerticalBarChart,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  type BlueprintQuestion,
  type BlueprintResponse,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

import { percentOf } from './analytics.labels';
import { AnalyticsCard } from './AnalyticsCard';
import { type BarRow, BarRows } from './EngagementCards';
import { type SegmentFilter } from './StudentDataModal';
import { useStudentData } from './useStudentData';
import { blueprintAnswerColumn, COMMON_COLUMNS } from './viewData.columns';

const { answers: strings } = appStrings.modules.coachingAnalytics.blueprint;
const { hover, viewData } = appStrings.modules.coachingAnalytics;

/** `calculator_key` of each visible Blueprint question. */
const QUESTION = {
  futureHome: 'future_home',
  homeBase: 'surrounding_location',
  futureLifestyle: 'future_lifestyle',
  futureFamily: 'future_family',
  childrensEducation: 'childrens_education',
  childcare: 'childcare',
  buildingWealth: 'building_wealth',
  jobOrCareerOptions: 'job_or_career_options',
  professionalGrowth: 'professional_growth',
  professionalRecognition: 'professional_recognition',
  workYouEnjoy: 'activities_you_enjoy',
  workYouDontEnjoy: 'activities_you_dont_like',
  sourcesOfMeaning: 'sources_of_meaning',
  workEnvironments: 'preferred_work_environments',
  workEnvironmentsToAvoid: 'work_environments_to_avoid',
  nextSteps: 'next_steps',
  caregiverFlexibility: 'caregiver_flexibility',
} as const;

/** A dropdown entry: one question's distribution, or a prefer/avoid pair split per option. */
type Choice = Readonly<
  { key: string; label?: string } & (
    | { kind: 'single'; questionKey: string }
    | { kind: 'paired'; preferKey: string; avoidKey: string }
  )
>;

const PERSONAL_CHOICES: readonly Choice[] = [
  { key: 'lifestyle', kind: 'single', questionKey: QUESTION.futureLifestyle },
  { key: 'wealth', kind: 'single', questionKey: QUESTION.buildingWealth },
  { key: 'home', kind: 'single', questionKey: QUESTION.futureHome },
  { key: 'home-base', kind: 'single', questionKey: QUESTION.homeBase },
  { key: 'family', kind: 'single', questionKey: QUESTION.futureFamily },
  { key: 'childcare', kind: 'single', questionKey: QUESTION.childcare },
  { key: 'childrens-education', kind: 'single', questionKey: QUESTION.childrensEducation },
  { key: 'caregiver', kind: 'single', questionKey: QUESTION.caregiverFlexibility },
];

const CAREER_CHOICES: readonly Choice[] = [
  {
    key: 'preferences',
    label: strings.careerPreferences,
    kind: 'paired',
    preferKey: QUESTION.workYouEnjoy,
    avoidKey: QUESTION.workYouDontEnjoy,
  },
  {
    key: 'environments',
    kind: 'paired',
    preferKey: QUESTION.workEnvironments,
    avoidKey: QUESTION.workEnvironmentsToAvoid,
  },
  { key: 'options', kind: 'single', questionKey: QUESTION.jobOrCareerOptions },
  { key: 'growth', kind: 'single', questionKey: QUESTION.professionalGrowth },
  { key: 'recognition', kind: 'single', questionKey: QUESTION.professionalRecognition },
];

const PAIR_SEGMENTS: readonly {
  key: 'prefer' | 'avoid';
  color: ColorToken;
  labelColor: ColorToken;
}[] = [
  { key: 'prefer', color: 'Brand/500', labelColor: 'Colors/Text/text-white' },
  { key: 'avoid', color: 'Brand/200', labelColor: 'Colors/Text/text-primary' },
];

const PAIR_BAR_HEIGHT = 24;

const questionByKey = (data: BlueprintResponse, key: string): BlueprintQuestion | undefined =>
  data.answers.questions.find((question) => question.key === key);

const questionsOf = (data: BlueprintResponse, choice: Choice): readonly BlueprintQuestion[] =>
  (choice.kind === 'single'
    ? [questionByKey(data, choice.questionKey)]
    : [questionByKey(data, choice.preferKey), questionByKey(data, choice.avoidKey)]
  ).filter((question): question is BlueprintQuestion => question !== undefined);

const choiceLabel = (data: BlueprintResponse, choice: Choice): string =>
  choice.label ?? questionsOf(data, choice)[0]?.title ?? choice.key;

const sameAnswer = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const optionSegment = (
  question: BlueprintQuestion,
  optionId: number,
  answer: string,
): SegmentFilter => ({
  label: viewData.segmentLabel(question.title, answer),
  where: [
    { field: 'blueprint_answers', key: String(question.id), op: 'eq', value: String(optionId) },
  ],
});

const questionInfo = (question: BlueprintQuestion, total: number): string[] => [
  strings.answered(question.answered, total),
  ...(question.is_multi_select ? [strings.multiSelectInfo] : []),
];

const QuestionBars = ({
  question,
  cardId,
  onSelect,
}: Readonly<{
  question: BlueprintQuestion;
  cardId: string;
  onSelect: (segment: SegmentFilter) => void;
}>) => (
  <VerticalBarChart
    ariaLabel={question.question}
    outlineColor="Colors/Base/Black"
    bars={question.options.map((option) => ({
      id: `${cardId}-option-${option.id}`,
      label: option.answer,
      value: option.count,
      tooltip: hover.share(option.answer, option.count, percentOf(option.count, question.answered)),
      onClick: () => onSelect(optionSegment(question, option.id, option.answer)),
    }))}
  />
);

/** Long option labels read better beside horizontal bars than beneath vertical ones. */
const QuestionBarRows = ({
  question,
  cardId,
  onSelect,
}: Readonly<{
  question: BlueprintQuestion;
  cardId: string;
  onSelect: (segment: SegmentFilter) => void;
}>) => {
  const rows: BarRow[] = question.options.map((option) => {
    const pct = percentOf(option.count, question.answered);

    return {
      id: `option-${option.id}`,
      label: option.answer,
      pct,
      value: option.count,
      tooltip: hover.count(option.answer, option.count),
      segment: optionSegment(question, option.id, option.answer),
    };
  });

  return <BarRows rows={rows} idPrefix={cardId} onSelect={onSelect} />;
};

const DONUT_COLORS: readonly ColorToken[] = [
  'Brand/700',
  'Brand/500',
  'Brand/300',
  'Warning/400',
  'Success/500',
  'Error/400',
  'Gray/400',
  'Colors/Base/Black',
];

/** Option shares as a donut, with a legend row per option that opens its students. */
const QuestionDonut = ({
  question,
  cardId,
  onSelect,
}: Readonly<{
  question: BlueprintQuestion;
  cardId: string;
  onSelect: (segment: SegmentFilter) => void;
}>) => {
  const select = (index: number) => {
    const option = question.options[index];

    if (option) onSelect(optionSegment(question, option.id, option.answer));
  };

  return (
    <Box sx={donutRowSx}>
      <Box sx={donutLegendSx}>
        {question.options.map((option, index) => (
          <LegendRow
            key={option.id}
            id={`${cardId}-option-${option.id}`}
            color={DONUT_COLORS[index % DONUT_COLORS.length]}
            label={option.answer}
            pct={percentOf(option.count, question.answered)}
            count={option.count}
            onClick={() => select(index)}
          />
        ))}
      </Box>
      <Box sx={donutWrapSx}>
        <DonutChart
          id={`${cardId}-donut`}
          variant={{
            type: 'multi',
            segments: question.options.map((option, index) => ({
              label: option.answer,
              value: option.count,
              color: DONUT_COLORS[index % DONUT_COLORS.length],
            })),
          }}
          size={200}
          onSegmentClick={select}
        />
      </Box>
    </Box>
  );
};

/** Options of the prefer question, each split by how many chose it versus its avoid twin. */
const PairedBars = ({
  prefer,
  avoid,
  cardId,
  onSelect,
}: Readonly<{
  prefer: BlueprintQuestion;
  avoid: BlueprintQuestion;
  cardId: string;
  onSelect: (segment: SegmentFilter) => void;
}>) => (
  <Box sx={pairedSx}>
    <Box sx={pairedLegendSx}>
      <LegendRow color={PAIR_SEGMENTS[0].color} label={prefer.title} />
      <LegendRow color={PAIR_SEGMENTS[1].color} label={avoid.title} />
    </Box>
    {prefer.options.map((option, index) => {
      const twin =
        avoid.options.find((candidate) => sameAnswer(candidate.answer, option.answer)) ??
        avoid.options[index];
      const counts = { prefer: option.count, avoid: twin?.count ?? 0 };
      const rowTotal = counts.prefer + counts.avoid;
      const segments: StackedBarSegment[] = PAIR_SEGMENTS.map((segment) => {
        const question = segment.key === 'prefer' ? prefer : avoid;
        const chosen = segment.key === 'prefer' ? option : twin;
        const pct = percentOf(counts[segment.key], rowTotal);

        return {
          key: segment.key,
          id: `${cardId}-${segment.key}-${option.id}`,
          pct,
          color: segment.color,
          labelColor: segment.labelColor,
          tooltip: hover.count(question.title, counts[segment.key]),
          onClick: chosen && (() => onSelect(optionSegment(question, chosen.id, chosen.answer))),
        };
      });

      return (
        <Box key={option.id} sx={pairedRowSx}>
          <Typography variant="text-sm-medium" sx={pairedRowLabelSx}>
            {option.answer}
          </Typography>
          <Box sx={pairedBarLineSx}>
            <Box sx={pairedBarWrapSx}>
              <StackedBar segments={segments} height={PAIR_BAR_HEIGHT} />
            </Box>
            <CountPill value={rowTotal} sx={pairedRowTotalSx} />
          </Box>
        </Box>
      );
    })}
  </Box>
);

/** One question's option distribution with its exact wording as the subtitle. */
const QuestionCard = ({
  data,
  questionKey,
  title,
  cardId,
  chart,
}: Readonly<{
  data: BlueprintResponse;
  questionKey: string;
  title: string;
  cardId: string;
  chart: 'rows' | 'donut';
}>) => {
  const question = questionByKey(data, questionKey);
  const columns = useMemo(
    () => (question ? [...COMMON_COLUMNS, blueprintAnswerColumn(question)] : COMMON_COLUMNS),
    [question],
  );
  const students = useStudentData(cardId, title, columns);

  if (!question) return null;

  return (
    <AnalyticsCard
      title={title}
      infoTitle={strings.infoHeading(title, question.question)}
      info={questionInfo(question, data.answers.total)}
      viewData={students}
    >
      {chart === 'donut' ? (
        <QuestionDonut question={question} cardId={cardId} onSelect={students.openSegment} />
      ) : (
        <QuestionBarRows question={question} cardId={cardId} onSelect={students.openSegment} />
      )}
    </AnalyticsCard>
  );
};

/** A group of questions behind one dropdown; the chart follows the selected question. */
const QuestionPickerCard = ({
  data,
  title,
  description,
  choices,
  cardId,
}: Readonly<{
  data: BlueprintResponse;
  title: string;
  /** Opens the info modal: what this group of questions asks about and why. */
  description: string;
  choices: readonly Choice[];
  cardId: string;
}>) => {
  const available = choices.filter((choice) => questionsOf(data, choice).length > 0);
  const [selectedKey, setSelectedKey] = useState(available[0]?.key ?? '');
  const choice = available.find((entry) => entry.key === selectedKey) ?? available[0];
  const questions = useMemo(() => (choice ? questionsOf(data, choice) : []), [data, choice]);
  const columns = useMemo(
    () => [...COMMON_COLUMNS, ...questions.map(blueprintAnswerColumn)],
    [questions],
  );
  const students = useStudentData(
    cardId,
    choice ? viewData.segmentLabel(title, choiceLabel(data, choice)) : title,
    columns,
  );

  if (!choice) return null;

  const [first, second] = questions;
  const info =
    choice.kind === 'paired' && second
      ? [
          description,
          strings.answered(first.answered, data.answers.total),
          strings.pairedInfo(first.title, second.title),
        ]
      : [description, ...questionInfo(first, data.answers.total)];

  return (
    <AnalyticsCard
      title={title}
      info={info}
      viewData={students}
      belowHeader={
        <Box sx={pickerBlockSx}>
          <Box sx={pickerSx}>
            <LabeledFilterSelect
              id={`${cardId}-question-select`}
              label={strings.questionLabel}
              hideLabel
              value={choice.key}
              options={available.map((entry) => ({
                value: entry.key,
                label: choiceLabel(data, entry),
              }))}
              onChange={setSelectedKey}
            />
          </Box>
          <Typography variant="text-sm-regular" sx={questionTextSx}>
            {strings.questionText(
              choice.kind === 'paired' && second
                ? `${first.question} ${second.question}`
                : first.question,
            )}
          </Typography>
        </Box>
      }
    >
      {choice.kind === 'paired' && second ? (
        <PairedBars prefer={first} avoid={second} cardId={cardId} onSelect={students.openSegment} />
      ) : (
        <QuestionBars question={first} cardId={cardId} onSelect={students.openSegment} />
      )}
    </AnalyticsCard>
  );
};

export const BlueprintAnswersDetail = ({ data }: Readonly<{ data: BlueprintResponse }>) => (
  <>
    <Box sx={pairRowSx}>
      <QuestionCard
        data={data}
        questionKey={QUESTION.nextSteps}
        title={strings.futurePlans}
        cardId="coaching-analytics-card-future-plans"
        chart="rows"
      />
      <QuestionCard
        data={data}
        questionKey={QUESTION.sourcesOfMeaning}
        title={strings.sourcesOfMeaning}
        cardId="coaching-analytics-card-sources-of-meaning"
        chart="donut"
      />
    </Box>
    <QuestionPickerCard
      data={data}
      title={strings.personalAspirations}
      description={strings.personalAspirationsInfo}
      choices={PERSONAL_CHOICES}
      cardId="coaching-analytics-card-personal-aspirations"
    />
    <QuestionPickerCard
      data={data}
      title={strings.professionalAspirations}
      description={strings.professionalAspirationsInfo}
      choices={CAREER_CHOICES}
      cardId="coaching-analytics-card-professional-aspirations"
    />
  </>
);

const pairRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'stretch',
  '@container (min-width: 900px)': {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  },
});

const donutRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column-reverse',
  alignItems: 'center',
  gap: backrs.spacing['spacing-2xl'],
  width: '100%',
  '@container (min-width: 650px)': {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

const donutLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  flex: 1,
  minWidth: 0,
  width: '100%',
});

const donutWrapSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const pickerBlockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const pickerSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: 360,
};

const questionTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const pairedSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const pairedLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-xl'],
});

const pairedRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  '@container (min-width: 650px)': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: backrs.spacing['spacing-lg'],
  },
});

const pairedRowLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  '@container (min-width: 650px)': {
    width: 240,
    flexShrink: 0,
  },
});

const pairedBarLineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  flex: 1,
  minWidth: 0,
});

const pairedBarWrapSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

const pairedRowTotalSx: SxProps<Theme> = {
  minWidth: 40,
  flexShrink: 0,
};
